import { type FC, useState } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import type { CountryProfile, CarrierGroup } from '@/types';

// ---------------------------------------------------------------------------
// Country Intelligence Panel
// Tabs: Overview (powerIndex) | Military | Leadership | Carriers
// ---------------------------------------------------------------------------

type TabId = 'overview' | 'military' | 'leaders' | 'carriers';

const NUCLEAR_BADGES: Record<string, { label: string; color: string }> = {
  possesses: { label: '☢ Nuclear', color: 'text-yellow-400' },
  pursuing:  { label: '⚠ Pursuing', color: 'text-orange-400' },
  umbrella:  { label: '🛡 Umbrella', color: 'text-sky-400' },
  none:      { label: 'Conv',       color: 'text-slate-500' },
};

const RISK_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  high:     'bg-orange-400',
  medium:   'bg-yellow-400',
  low:      'bg-emerald-400',
};

const GEO_BADGE: Record<string, { label: string; color: string }> = {
  'pro-western': { label: 'West',    color: 'text-sky-400' },
  'pro-russian': { label: 'Russia',  color: 'text-red-400' },
  'pro-chinese': { label: 'China',   color: 'text-rose-500' },
  'non-aligned': { label: 'Neutral', color: 'text-slate-400' },
  'contested':   { label: 'Split',   color: 'text-purple-400' },
};

// ─── Overview Tab ───────────────────────────────────────────────────────────

const OverviewTab: FC<{ profiles: CountryProfile[] }> = ({ profiles }) => {
  const sorted = [...profiles].sort((a, b) => b.powerIndex - a.powerIndex);

  return (
    <div className="space-y-0.5">
      {sorted.map((c) => {
        const nuc = NUCLEAR_BADGES[c.nuclearStatus];
        const geo = GEO_BADGE[c.geopoliticalStance] ?? { label: c.geopoliticalStance, color: 'text-slate-400' };
        return (
          <div key={c.code} className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-800/40 transition-colors group">
            {/* Flag + name */}
            <span className="text-sm shrink-0">{c.flagEmoji}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-semibold text-slate-200 truncate">{c.name}</span>
                <span className={clsx('text-[8px] shrink-0', nuc.color)}>{nuc.label}</span>
                <span className={clsx('text-[8px] shrink-0', geo.color)}>{geo.label}</span>
              </div>
              {/* Power Index bar */}
              <div className="flex items-center gap-1 mt-0.5">
                <div className="flex-1 h-1 bg-slate-800 rounded overflow-hidden">
                  <div
                    className={clsx(
                      'h-full rounded transition-all',
                      c.powerIndex >= 80 ? 'bg-red-500' :
                      c.powerIndex >= 60 ? 'bg-orange-400' :
                      c.powerIndex >= 40 ? 'bg-yellow-400' : 'bg-slate-500',
                    )}
                    style={{ width: `${c.powerIndex}%` }}
                  />
                </div>
                <span className="text-[8px] text-slate-500 shrink-0 w-5 text-right">{c.powerIndex}</span>
              </div>
            </div>
            {/* Risk dot */}
            <span className={clsx('w-2 h-2 rounded-full shrink-0', RISK_DOT[c.instabilityRisk] ?? 'bg-slate-600')} />
          </div>
        );
      })}
    </div>
  );
};

// ─── Military Tab ────────────────────────────────────────────────────────────

const MilitaryTab: FC<{ profiles: CountryProfile[] }> = ({ profiles }) => {
  const sorted = [...profiles].sort((a, b) => b.military.budgetBillionUSD - a.military.budgetBillionUSD);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[9px] text-slate-400">
        <thead>
          <tr className="text-[8px] uppercase tracking-wide text-slate-600 border-b border-slate-800">
            <th className="text-left py-1 px-1">Country</th>
            <th className="text-right py-1 px-1">Troops</th>
            <th className="text-right py-1 px-1">CV</th>
            <th className="text-right py-1 px-1">Subs</th>
            <th className="text-right py-1 px-1">AC</th>
            <th className="text-right py-1 px-1">$B</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => (
            <tr key={c.code} className="border-b border-slate-800/40 hover:bg-slate-800/30">
              <td className="py-0.5 px-1">
                <span className="mr-1">{c.flagEmoji}</span>
                <span className="text-slate-300">{c.code}</span>
              </td>
              <td className="text-right py-0.5 px-1 text-slate-300">
                {c.military.activeTroops >= 1000
                  ? `${(c.military.activeTroops / 1000).toFixed(1)}M`
                  : `${c.military.activeTroops}K`}
              </td>
              <td className="text-right py-0.5 px-1">
                {c.military.aircraftCarriers > 0 ? (
                  <span className="text-sky-400 font-bold">{c.military.aircraftCarriers}</span>
                ) : (
                  <span className="text-slate-700">—</span>
                )}
              </td>
              <td className="text-right py-0.5 px-1 text-slate-300">{c.military.submarines}</td>
              <td className="text-right py-0.5 px-1 text-slate-300">{c.military.combatAircraft.toLocaleString()}</td>
              <td className={clsx('text-right py-0.5 px-1 font-semibold',
                c.military.budgetBillionUSD >= 500 ? 'text-red-400' :
                c.military.budgetBillionUSD >= 100 ? 'text-orange-400' : 'text-slate-400'
              )}>
                {c.military.budgetBillionUSD >= 1000
                  ? `${(c.military.budgetBillionUSD / 1000).toFixed(1)}T`
                  : c.military.budgetBillionUSD}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-1 pt-1 text-[8px] text-slate-700 italic">CV=Carriers · AC=Combat Aircraft · $B=Budget</div>
    </div>
  );
};

// ─── Leadership Tab ──────────────────────────────────────────────────────────

const LeadersTab: FC<{ profiles: CountryProfile[] }> = ({ profiles }) => {
  const sorted = [...profiles].sort((a, b) => {
    const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
    return (order[a.leader.riskLevel] ?? 4) - (order[b.leader.riskLevel] ?? 4);
  });

  return (
    <div className="space-y-1">
      {sorted.map((c) => (
        <div key={c.code} className="border border-slate-800/40 rounded p-1.5 hover:border-slate-700/60 transition-colors">
          <div className="flex items-start gap-1.5">
            <span className="text-sm shrink-0">{c.flagEmoji}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[10px] font-semibold text-slate-200">{c.leader.name}</span>
                <span className="text-[8px] text-slate-500">{c.leader.title}</span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className={clsx(
                  'text-[8px] font-bold px-1 rounded',
                  c.leader.riskLevel === 'critical' ? 'bg-red-950/60 text-red-400' :
                  c.leader.riskLevel === 'high'     ? 'bg-orange-950/60 text-orange-400' :
                  c.leader.riskLevel === 'medium'   ? 'bg-yellow-950/60 text-yellow-400' :
                  'bg-emerald-950/60 text-emerald-400',
                )}>
                  {c.leader.riskLevel.toUpperCase()}
                </span>
                <span className="text-[8px] text-slate-600">Since {c.leader.sinceYear} · Age {c.leader.age}</span>
                <span className={clsx('text-[8px]', RISK_DOT[c.leader.successionRisk] ? 'text-slate-500' : 'text-slate-600')}>
                  Succession: <span className={clsx('font-medium',
                    c.leader.successionRisk === 'critical' ? 'text-red-400' :
                    c.leader.successionRisk === 'high'     ? 'text-orange-400' : 'text-slate-400',
                  )}>{c.leader.successionRisk}</span>
                </span>
              </div>
              <p className="text-[9px] text-slate-500 mt-0.5 leading-relaxed">{c.leader.riskNotes}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Carriers Tab ────────────────────────────────────────────────────────────

const CarriersTab: FC<{ carrierGroups: CarrierGroup[] }> = ({ carrierGroups }) => {
  const COUNTRY_FLAG: Record<string, string> = {
    USA: '🇺🇸', China: '🇨🇳', UK: '🇬🇧', France: '🇫🇷', India: '🇮🇳',
  };

  return (
    <div className="space-y-1">
      {carrierGroups.map((cg) => (
        <div key={cg.id} className={clsx(
          'border rounded p-1.5',
          cg.country === 'USA'   ? 'border-sky-900/50 bg-sky-950/10' :
          cg.country === 'China' ? 'border-red-900/50 bg-red-950/10' :
          'border-slate-800/50 bg-slate-900/20',
        )}>
          <div className="flex items-start gap-1.5">
            <span className="text-sm shrink-0">{COUNTRY_FLAG[cg.country] ?? '🏳'}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold text-slate-100">{cg.carrierName}</span>
                <span className={clsx(
                  'text-[8px] px-1 rounded',
                  cg.country === 'USA'   ? 'bg-sky-900/60 text-sky-400' :
                  cg.country === 'China' ? 'bg-red-900/60 text-red-400' :
                  'bg-slate-800 text-slate-400',
                )}>{cg.country}</span>
              </div>
              <div className="text-[9px] text-slate-400 mt-0.5">{cg.groupName}</div>
              <div className="mt-0.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[8px]">
                <div><span className="text-slate-600">Air Wing </span><span className="text-slate-300">{cg.airWing}</span></div>
                <div><span className="text-slate-600">Region </span><span className="text-slate-300">{cg.region}</span></div>
                <div><span className="text-slate-600">Destroyers </span><span className="text-slate-400">{cg.destroyers.length}</span></div>
                <div><span className="text-slate-600">Submarines </span><span className="text-slate-400">{cg.submarines.length}</span></div>
              </div>
              <div className="mt-0.5 text-[8px] text-slate-500 italic">{cg.mission}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

export const CountryIntelPanel: FC = () => {
  const countryProfiles = useAppStore((s) => s.countryProfiles);
  const carrierGroups = useAppStore((s) => s.carrierGroups);
  const [tab, setTab] = useState<TabId>('overview');

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'overview',  label: 'Index',    count: countryProfiles.length },
    { id: 'military',  label: 'Military' },
    { id: 'leaders',   label: 'Leaders' },
    { id: 'carriers',  label: 'Carriers', count: carrierGroups.length },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-widest text-sky-400">🌍 Country Intel</span>
        <span className="text-[9px] text-slate-600 italic">Strategic DB</span>
      </div>

      {/* Tabs */}
      <div className="px-3 flex gap-1 shrink-0">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors border flex items-center gap-1',
              tab === t.id
                ? 'bg-sky-900/50 text-sky-300 border-sky-800'
                : 'text-slate-500 border-slate-800 hover:text-slate-300',
            )}
          >
            {t.label}
            {t.count !== undefined && (
              <span className={clsx('text-[7px] px-0.5 rounded', tab === t.id ? 'text-sky-400' : 'text-slate-600')}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin">
        {tab === 'overview'  && <OverviewTab  profiles={countryProfiles} />}
        {tab === 'military'  && <MilitaryTab  profiles={countryProfiles} />}
        {tab === 'leaders'   && <LeadersTab   profiles={countryProfiles} />}
        {tab === 'carriers'  && <CarriersTab  carrierGroups={carrierGroups} />}
      </div>
    </div>
  );
};
